import {inject, Injectable, isDevMode, signal} from '@angular/core';
import * as CookieConsent from 'vanilla-cookieconsent';
import {Translation} from 'vanilla-cookieconsent';
import {BaseApi} from './base.api';
import {EEndpointApi} from '@shared/enums/endpoint-api.enum';
import {TUserId} from '@shared/interfaces/user';
import {ELanguageLabel, TLanguageLabelOrString} from '@shared/enums/language';
import {ENDPOINT_BASE_SITE} from '@shared/consts/base';
import {TLanguage} from '@shared/interfaces/language';

@Injectable({
  providedIn: 'root',
})
export class CookieTrustService extends BaseApi {
  private BASE_SITE = inject(ENDPOINT_BASE_SITE);
  readonly COOKIE_NAME = 'fbfast_cc_cookie';
  private ANALYTICS_GRANTED = false;
  private userId?: TUserId = null;
  private lang: TLanguage;

  readonly responseReceived = signal(false);

  setUserId(userId?: TUserId): void {
    this.userId = userId;
  }

  run(lang: TLanguage): void {
    this.lang = lang;
    CookieConsent.run(this.getConfigOptions()).then(() => {
      // console.log('CookieConsent instance');
    });
  }

  private getConfigOptions(): CookieConsent.CookieConsentConfig {
    return {
      revision: 1,
      onChange: ({cookie}) => this.consentReceipt(cookie),
      onFirstConsent: ({cookie}) => this.consentReceipt(cookie),
      onConsent: ({cookie}) => this.onConsent(cookie),
      lazyHtmlGeneration: true,
      disablePageInteraction: true,
      cookie: {
        name: this.COOKIE_NAME,
        domain: !isDevMode() ? '.fbfast.io' : '.fbfast-local.ua',
      },
      guiOptions: {
        consentModal: {
          layout: 'box',
          position: 'middle center',
          equalWeightButtons: true,
          flipButtons: false,
        },
        preferencesModal: {
          layout: 'box',
          position: 'right',
          equalWeightButtons: true,
          flipButtons: false,
        },
      },
      categories: {
        necessary: {
          enabled: true, // не можна вимкнути
          readOnly: true, // this category cannot be disabled
        },
        functionality: {
          enabled: false,
          readOnly: false,
        },
        analytics: {
          enabled: false,
          readOnly: false,
          autoClear: {
            reloadPage: false,
            cookies: [
              {
                name: /^_ga/, // regex: match all cookies starting with '_ga'
              },
              {
                name: '_gid', // string: exact cookie name
              },
              {
                name: 'CLID', // www.clarity.ms
              },
              {
                name: 'MUID', // .clarity.ms
              },
            ],
          },
        },
      },
      language: {
        // default: ELanguageLabel.EN,
        default: this.lang,
        autoDetect: 'document',
        translations: {
          // ["necessary", "analytics", "functionality"]
          uk: async () => this.translationConsent(ELanguageLabel.UK),
          fr: async () => this.translationConsent(ELanguageLabel.FR),
          de: async () => this.translationConsent(ELanguageLabel.DE),
          es: async () => this.translationConsent(ELanguageLabel.ES),
          en: async () => this.translationConsent(ELanguageLabel.EN),
        },
      },
    };
  }

  private async translationConsent(lang: TLanguageLabelOrString): Promise<Translation> {
    const res = await fetch(`${this.BASE_SITE}/assets/cookie/consent.${lang}.json`, {
      credentials: 'same-origin',
    });
    if (!res.ok) {
      throw new Error(`Failed to fetch translation for language ${lang}`);
    }
    return await res.json();
  }

  private onConsent(cookie: CookieConsent.CookieValue): void {
    this.responseReceived.set(true);

    const enabled = (cat: string) => {
      return (cookie.categories || []).includes(cat);
    };

    // аналітика: GA4 + Ads (моделювання дозволене, але cookies тільки зі згодою)
    const analyticsGranted = enabled('analytics');

    if (analyticsGranted == this.ANALYTICS_GRANTED || !('gtag' in window)) {
      return;
    }

    this.ANALYTICS_GRANTED = analyticsGranted;
    // функціональні кукі (твоя категорія 'functionality')
    const functionalityGranted = enabled('functionality');

    // Оновлення Consent Mode v2
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    gtag('consent', 'update', {
      ad_storage: analyticsGranted ? 'granted' : 'denied',
      analytics_storage: analyticsGranted ? 'granted' : 'denied',
      personalization_storage: analyticsGranted ? 'granted' : 'denied',
      functionality_storage: functionalityGranted ? 'granted' : 'denied',
      security_storage: 'granted',
    });
  }

  private consentReceipt(cookie: CookieConsent.CookieValue): void {
    super.post<{receipt: string}>(EEndpointApi.ConsentReceipt, {...cookie, userId: this.userId}).subscribe((response) => {
      console.log(`Response Received: ${response?.receipt || 'N/A'}`);
    });
  }
}
